import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Plus, Pencil, Trash2, Shield, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODULES = [
  'dashboard',
  'users',
  'universities',
  'packages',
  'questions',
  'interviews',
  'applications',
  'roles'
];

const PERMISSIONS = ['view', 'create', 'edit', 'delete'];

export default function AdminRoles() {
  const { token } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: {}
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoles(response.data);
    } catch (error) {
      toast.error('Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingRole) {
        await axios.put(
          `${API_URL}/api/roles/${editingRole.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Role updated successfully');
      } else {
        await axios.post(
          `${API_URL}/api/roles`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Role created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchRoles();
    } catch (error) {
      toast.error('Failed to save role');
    }
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description,
      permissions: role.permissions
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this role?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/roles/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Role deleted');
      fetchRoles();
    } catch (error) {
      toast.error('Failed to delete role');
    }
  };

  const resetForm = () => {
    setEditingRole(null);
    setFormData({
      name: '',
      description: '',
      permissions: {}
    });
  };

  const togglePermission = (module, permission) => {
    setFormData(prev => {
      const modulePerms = prev.permissions[module] || [];
      const newModulePerms = modulePerms.includes(permission)
        ? modulePerms.filter(p => p !== permission)
        : [...modulePerms, permission];
      
      return {
        ...prev,
        permissions: {
          ...prev.permissions,
          [module]: newModulePerms
        }
      };
    });
  };

  const hasPermission = (module, permission) => {
    return formData.permissions[module]?.includes(permission) || false;
  };

  const countPermissions = (permissions) => {
    return Object.values(permissions).reduce((acc, perms) => acc + perms.length, 0);
  };

  return (
    <DashboardLayout role="super_admin">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="font-heading font-bold text-2xl">Roles & RBAC</h2>
            <p className="text-muted-foreground">Manage admin roles and permissions</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-lime-500 hover:bg-lime-600 text-white" data-testid="add-role">
                <Plus className="w-4 h-4 mr-2" />
                Create Role
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingRole ? 'Edit Role' : 'Create Role'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Role Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Content Manager"
                      required
                      data-testid="role-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Input
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Brief description of this role"
                      required
                      data-testid="role-description"
                    />
                  </div>
                </div>

                {/* Permissions Matrix */}
                <div className="space-y-4">
                  <Label>Permissions</Label>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Module</TableHead>
                          {PERMISSIONS.map(perm => (
                            <TableHead key={perm} className="text-center capitalize">{perm}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {MODULES.map(module => (
                          <TableRow key={module}>
                            <TableCell className="font-medium capitalize">{module}</TableCell>
                            {PERMISSIONS.map(perm => (
                              <TableCell key={perm} className="text-center">
                                <Checkbox
                                  checked={hasPermission(module, perm)}
                                  onCheckedChange={() => togglePermission(module, perm)}
                                  data-testid={`perm-${module}-${perm}`}
                                />
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-lime-500 hover:bg-lime-600 text-white" data-testid="save-role">
                    {editingRole ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Roles List */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Shield className="w-5 h-5 text-lime-500" />
                Roles
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">Loading...</div>
              ) : roles.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No roles created yet</div>
              ) : (
                <div className="space-y-3">
                  {roles.map((role) => (
                    <div 
                      key={role.id} 
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                      data-testid={`role-item-${role.id}`}
                    >
                      <div>
                        <p className="font-medium">{role.name}</p>
                        <p className="text-sm text-muted-foreground">{role.description}</p>
                        <Badge variant="secondary" className="mt-1 text-xs">
                          {countPermissions(role.permissions)} permissions
                        </Badge>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(role)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(role.id)} className="text-destructive">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Info */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Users className="w-5 h-5 text-lime-500" />
                About RBAC
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                Role-Based Access Control (RBAC) allows you to create custom roles with specific permissions for admin team members.
              </p>
              <div className="space-y-2">
                <p className="font-medium">Available Modules:</p>
                <div className="flex flex-wrap gap-2">
                  {MODULES.map(module => (
                    <Badge key={module} variant="outline" className="capitalize">{module}</Badge>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <p className="font-medium">Permission Levels:</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li><strong>View:</strong> Can see data and reports</li>
                  <li><strong>Create:</strong> Can add new records</li>
                  <li><strong>Edit:</strong> Can modify existing records</li>
                  <li><strong>Delete:</strong> Can remove records</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
